import { clsx } from 'clsx';
import { Spin } from 'antd';
import { SaveFilled } from '@ant-design/icons';
import { useLayoutEffect, useState } from 'react';
import { useNotify, useRefresh, useResourceContext } from 'react-admin';
import { Button, Dialog, DialogActions, DialogTitle } from '@mui/material';
import { Dealer, DealerDocumentProps, DealerDocumentRequest, DealerPartial, DealerType } from '@types';

import { dealerHttp } from '@network';
import { BuyerForm } from '../BuyerForm';
import { axiosErrorToString } from '@utils';
import { Footer } from './components/Footer';
import { MergeForm } from '../MergeDealersForm';
import cls from './buyer-creator.module.css';

const PHONE_MASK = '+52 (___) ___-____';

interface Props {
  type: 'create' | 'edit';
  close: () => void;
  values: Dealer | null;
}

const initialFormValues: DealerPartial = {
  name: '',
  phone: '',
  email: '',
  image: '',
  tier: 'C',
  cropNames: [],
  activationId: null,
  ownerId: null,
  cropsIbm: [],
  isCompany: true,
};

export const BuyerCreator = ({ type, close, values }: Props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [documents, setDocuments] = useState<DealerDocumentProps[]>([]);
  const [formValues, setFormValues] = useState<Dealer | DealerPartial>(values || initialFormValues);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const dealerType = resource === 'dealer' ? DealerType.BUYER
    : resource === 'dealer-retailer' ? DealerType.RETAILER : DealerType.ASSOCIATE;
  const dealerTitle = dealerType.slice(0, 1).toUpperCase() + dealerType.slice(1);

  const submit = async () => {
    if ((formValues.name || '').length === 0) {
      setShowErrors(true);
      return;
    }

    const params: DealerPartial = {
      name: formValues.name || '',
      phone: formValues.phone === PHONE_MASK ? '' : formValues.phone,
      image: formValues.image || '',
      email: formValues.email || '',
      tier: formValues.tier || 'C',
      cropNames: formValues.cropNames || [],
      activationId: formValues.activationId || null,
      priorityId: formValues.priorityId || null,
      segment: formValues.segment || null,
      statusId: formValues.statusId || null,
      ownerId: formValues.ownerId || null,
      website: formValues.website || null,
      state: formValues.state || null,
      size: formValues.size || null,
      farmerCount: formValues.farmerCount || null,
      closeDate: formValues.closeDate || null,
      source: formValues.source || null,
      ibm: formValues.ibm || null,
      ibms: formValues.ibms || [],
      states: formValues.states || [],
      lastUpdatedAt: formValues.lastUpdatedAt || null,
      lastUpdatedName: formValues.lastUpdatedName || null,
      ciec: formValues.ciec || null,
      financialVol: formValues.financialVol || 0,
      sowingDate: formValues.sowingDate || null,
      harvestDate: formValues.harvestDate || null,
      yearsExp: formValues.yearsExp || null,
      paymentType: formValues.paymentType || null,
      loanType: formValues.loanType || null,
      saleScope: formValues.saleScope || null,
      supplierLoan: formValues.supplierLoan || null,
      clientLoan: formValues.clientLoan || null,
      mktStrategy: formValues.mktStrategy || null,
      storeCount: formValues.storeCount || null,
      pCategories: formValues.pCategories || [],
      pBrands: formValues.pBrands || [],
      cropsIbm: formValues.cropsIbm || [],
      isCompany: formValues.isCompany !== null && formValues.isCompany !== undefined
        ? formValues.isCompany : true,
    };

    try {
      setIsLoading(true);
      const files: DealerDocumentRequest[] = documents.map(d => ({
        id: null, file: d.preview, name: d.originalName,
      }));
      await dealerHttp.create(params, files, resource);

      refresh();
      closeHandler();
    } catch (e) {
      notify(axiosErrorToString(e));
    } finally {
      setIsLoading(false);
    }
  };

  const update = async (): Promise<void> => {
    if (!('id' in formValues)) {
      setShowErrors(true);

      return;
    }

    if (formValues.name?.length === 0) {
      setShowErrors(true);

      return;
    }

    const params: Dealer = {
      ...formValues,
      phone: formValues.phone === PHONE_MASK ? '' : formValues.phone,
    };

    try {
      setIsLoading(true);
      const files: DealerDocumentRequest[] = documents.map(d => ({
        id: d.fileId || null, file: d.preview || null, name: d.originalName || '',
      }));
      await dealerHttp.update(params, files, resource);

      refresh();
    } catch (e) {
      notify(axiosErrorToString(e));
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async () => {
    if (!('id' in formValues)) {
      setShowErrors(true);

      return;
    }

    try {
      setIsLoading(true);
      await dealerHttp.remove(formValues.id, resource);

      refresh();
      closeHandler();
    } catch (e) {
      notify(axiosErrorToString(e));
    } finally {
      setIsLoading(false);
    }
  };

  const closeHandler = () => {
    setFormValues(initialFormValues);
    close();
  };

  const salesforceSync = async () => {
    if (!('id' in formValues)) {
      setShowErrors(true);

      return;
    }

    try {
      setIsLoading(true);
      await dealerHttp.salesforceSync(formValues.id);
    } catch (e) {
      notify(axiosErrorToString(e));
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    document.body.className = 'noscroll';

    if (values && values.documents?.length) {
      setDocuments((values.documents || []).map(d => ({
        idx: `${d.id}-${Date.now()}.toString()`,
        fileId: d.fileId,
        file: null,
        isPdf: d.isPdf,
        preview: '',
        adminUrl: d.adminUrl,
        originalName: '',
      })));
    }

    return () => {
      document.body.className = '';
    };
  }, []);

  return (
    <div className={cls.block}>
      <div className={cls.wrap}>
        <div className={cls.header}>
          <div className={cls.headerId}>{
            // @ts-ignore
            formValues.id ? `ID: ${formValues.id}` : ''
          }</div>
          <div className={clsx(cls.headerTitle, cls.title)}>
            {type === 'create' && `Create a new ${dealerTitle}`}
            {type === 'edit' && dealerTitle}
          </div>
          <div className={cls.headerClose}>
            <div className={cls.headerCloseIcon} onClick={closeHandler}>
              <svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" viewBox="0 0 35 35" fill="none">
                <path d="M17.5039 0C7.83826 0 0 7.83826 0 17.5039C0 27.1695 7.83826 35.0078 17.5039 35.0078C27.1695 35.0078 35.0078 27.1695 35.0078 17.5039C34.9922 7.83826 27.1617 0.00777605 17.5039 0Z" fill="#002A77" />
                <path d="M24.0903 10.9101C23.787 10.6068 23.2971 10.6068 22.9938 10.9101L17.5039 16.4L12.0141 10.9101C11.7108 10.6068 11.2209 10.6068 10.9176 10.9101C10.6144 11.2133 10.6144 11.7032 10.9176 12.0065L16.4075 17.4964L10.9176 22.9863C10.6144 23.2895 10.6144 23.7794 10.9176 24.0827C11.2209 24.386 11.7108 24.386 12.0141 24.0827L17.5039 18.5928L22.9938 24.0827C23.2971 24.386 23.787 24.386 24.0903 24.0827C24.3935 23.7794 24.3935 23.2895 24.0903 22.9863L18.6004 17.4964L24.0903 12.0065C24.3935 11.7032 24.3935 11.2133 24.0903 10.9101Z" fill="#FFFFFF" />
              </svg>
            </div>
          </div>
        </div>

        <BuyerForm
          formValues={formValues}
          setFormValues={setFormValues}
          showErrors={showErrors}
          dealerType={dealerType}
          documents={documents}
          setDocuments={setDocuments}
          loading={isLoading}
        />

        <Footer formValues={formValues} />

        {type === 'create' && (
          <div className={cls.actions}>
            <Button className={clsx(cls.bottomBtn, cls.submit)}
              variant="contained"
              onClick={submit}
              disabled={isLoading}>
              {isLoading && <Spin />}
              Add {dealerTitle}
            </Button>
          </div>
        )}

        {type === 'edit' && (
          <div className={cls.actions}>
            <div className={clsx(cls.actionsRow, cls.actionsTop)}>
              <Button
                className={clsx(cls.bottomBtn, cls.delete)}
                variant="contained"
                onClick={() => setIsDialogOpen(true)}
                disabled={isLoading}>Delete</Button>

              {values
                ? <MergeForm
                  dealer={values}
                  isBig
                  btnClass={clsx(cls.bottomBtn, cls.mergeBtn)}
                  closeSidebar={close} />
                : null}

              <Button
                className={clsx(cls.bottomBtn, cls.salesForce)}
                onClick={salesforceSync}
                variant="contained"
                disabled={isLoading}>Salesforce Sync</Button>

              <Button
                className={clsx(cls.bottomBtn, cls.update)}
                variant="contained"
                onClick={update}
                disabled={isLoading}
              >
                {isLoading
                  ? <Spin className={cls.actionIcon} />
                  : <SaveFilled className={cls.actionIcon} />} Save
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className={cls.overlay} onClick={closeHandler} />

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
        <DialogActions>
          <Button onClick={remove} autoFocus>Yes</Button>
          <Button onClick={() => setIsDialogOpen(false)}>No</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
